import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Label } from "../../../../components/ui/label";
import { DatePicker } from "../../../../components/ui/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { useAuth } from "../../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../../app/permissions";
import { adminUsersService } from "../../../../services/adminUsers";

export function StepEvent({ form, setForm, t }) {
  const { t: tt } = useTranslation();
  const { user } = useAuth();
  const canOnBehalf = hasPermission(user, Permission.ADMIN_USERS_MANAGE);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!canOnBehalf) return;
    adminUsersService
      .summary()
      .then((d) => setUsers(d.items || []))
      .catch(() => setUsers([]));
  }, [canOnBehalf]);

  // Default the picker to the admin themselves once we know who they are.
  useEffect(() => {
    if (!canOnBehalf) return;
    if (!form.on_behalf_of_user_id && user?.id) {
      setForm((f) => ({ ...f, on_behalf_of_user_id: user.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOnBehalf, user]);

  return (
    <div className="space-y-5" data-testid="step-event">
      {canOnBehalf && (
        <div className="space-y-1.5" data-testid="wizard-on-behalf-wrapper">
          <Label>{tt("wizard.on_behalf_of")}</Label>
          <Select
            value={form.on_behalf_of_user_id || user?.id || ""}
            onValueChange={(v) => setForm({ ...form, on_behalf_of_user_id: v })}
          >
            <SelectTrigger
              data-testid="wizard-on-behalf-select"
              className="h-11 bg-surface border-border-subtle max-w-md"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`wizard-on-behalf-option-${u.id}`}>
                  {u.name}{u.email ? ` — ${u.email}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-text-secondary">
            {tt("wizard.on_behalf_hint")}
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>{t("wizard.event_name")}</Label>
        <Input
          value={form.event_name}
          onChange={(e) => setForm({ ...form, event_name: e.target.value })}
          data-testid="wizard-event-name"
          placeholder={t("wizard.event_name_placeholder")}
          className="h-11 bg-surface border-border-subtle focus-visible:ring-champagne"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t("wizard.from_date")}</Label>
          <DatePicker
            value={form.from_date}
            required
            // Bug 4: block past dates.
            minDate={(() => { const d = new Date(); d.setHours(0,0,0,0); return d; })()}
            onChange={(d) => {
              setForm((f) => {
                const next = { ...f, from_date: d };
                // Auto-adjust Until when it now sits before From.
                if (d && f.until_date && f.until_date < d) next.until_date = d;
                return next;
              });
            }}
            testId="wizard-from-date"
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("wizard.until_date")}</Label>
          <DatePicker
            value={form.until_date}
            required
            // Bug 4: Until can't be before From (falls back to today
            // when no From is selected yet).
            minDate={(() => {
              if (form.from_date) return new Date(`${form.from_date}T00:00:00`);
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              return d;
            })()}
            onChange={(d) => setForm({ ...form, until_date: d })}
            testId="wizard-until-date"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>{t("wizard.bottles_count")}</Label>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={form.bottles_count}
          onChange={(e) => {
            // A17: pure text-only input. Strip non-digits so paste of
            // decimals / letters is silently normalised. Empty stays
            // empty so the field can be cleared.
            const digits = e.target.value.replace(/\D/g, "");
            setForm({ ...form, bottles_count: digits });
          }}
          data-testid="wizard-bottles-count"
          className="h-11 bg-surface border-border-subtle focus-visible:ring-champagne max-w-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t("wizard.remarks")}</Label>
        <Textarea
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          rows={3}
          maxLength={500}
          data-testid="wizard-remarks"
          className="bg-surface border-border-subtle focus-visible:ring-champagne"
        />
      </div>
    </div>
  );
}
